import {
    StyledLabel,
    StyledSelectInput,
    StyledTextInput,
} from "@aureskonnect/react-ui";
import classnames from "classnames";
import React from "react";
import { AvGroup } from "availity-reactstrap-validation";
import { useTranslation } from "react-i18next";
import Switch from "react-switch";
import { numberPositiveMatch } from "@constants/index";

import { useMoneticsForm } from "./MoneticsFormContext";
import { VALINA_REFERENCE_OPTIONS } from "./moneticsConstants";
import { FieldError } from "./components/FieldError";

export function ValinaConfig() {
    const { form, props } = useMoneticsForm();
    const {
        port,
        portSelectInput,
        ports,
        setPortSelectInput,
        setPort,
        speedSelectInput,
        speed,
        speeds,
        setSpeedSelectInput,
        setSpeed,
        selectInputPM,
        list_payment_method,
        paymentMethod,
        setPaymentMethod,
        setSelectInputPM,
        inputTimeout,
        setTimeOut,
        setInputTimeout,
        timeout,
        kiss,
        setKiss,
        setReference,
        reference,
    } = form;
    const { isConsult, setIsEdited } = props;
    const { t } = useTranslation();

    const valinaReferencesArray: {
        label: string;
        value: string;
    }[] = VALINA_REFERENCE_OPTIONS.map((element: string) => {
        return {
            label: element,
            value: element,
        };
    });
    return (
        <React.Fragment>
            <AvGroup>
                <StyledLabel htmlFor="text" className="mt-3">
                    {t("Reference")}
                </StyledLabel>

                <StyledSelectInput
                    data-testid="monetics-valina-reference-select"
                    className={classnames("mnt_inp_reference", {
                        readOnly__clz: isConsult,
                    })}
                    onChange={(e: any) => {
                        setReference(e.value);
                        setIsEdited(true);
                    }}
                    value={
                        reference === ""
                            ? null
                            : {
                                  label: reference,
                                  value: reference,
                              }
                    }
                    options={valinaReferencesArray}
                    name="reference"
                    placeholder={t("Select")}
                    noOptionsMessage={() => t("No options")}
                />
            </AvGroup>
            <AvGroup>
                <StyledLabel className="mt-3 required__clz" htmlFor="text">
                    {t("Port number")}
                </StyledLabel>

                <StyledSelectInput
                    data-testid="monetics-valina-port-select"
                    className={classnames("mnt_inp_portNumber", {
                        invalid__clz: portSelectInput,
                        readOnly__clz: isConsult,
                    })}
                    value={
                        port === ""
                            ? null
                            : {
                                  label: port,
                                  value: port,
                              }
                    }
                    id="portNumber"
                    name="portNumber"
                    options={ports}
                    onChange={(e: any) => {
                        setPortSelectInput(false);
                        setPort(e.label);
                        setIsEdited(true);
                    }}
                    placeholder={t("Select")}
                    noOptionsMessage={() => t("No options")}
                    maxMenuHeight="80px"
                />

                {portSelectInput && (
                    <FieldError>
                        {t("Please select a port number")}
                    </FieldError>
                )}
            </AvGroup>
            <AvGroup>
                <StyledLabel className="mt-3 required__clz" htmlFor="text">
                    {t("Speed")}
                </StyledLabel>

                <StyledSelectInput
                    data-testid="monetics-valina-speed-select"
                    className={classnames("mnt_inp_speed", {
                        invalid__clz: speedSelectInput,
                        readOnly__clz: isConsult,
                    })}
                    value={
                        speed === ""
                            ? null
                            : {
                                  label: speed,
                                  value: speed,
                              }
                    }
                    id="speed"
                    name="speed"
                    options={speeds}
                    onChange={(e: any) => {
                        setSpeedSelectInput(false);
                        setSpeed(e.label);
                        setIsEdited(true);
                    }}
                    placeholder={t("Select")}
                    noOptionsMessage={() => t("No options")}
                    maxMenuHeight="80px"
                />
                {speedSelectInput && (
                    <FieldError>
                        {t("Please select a speed")}
                    </FieldError>
                )}
            </AvGroup>
            <AvGroup>
                <StyledLabel className="mt-3 required__clz" htmlFor="text">
                    {t("Method of payment")}
                </StyledLabel>
                <StyledSelectInput
                    data-testid="monetics-valina-payment-method-select"
                    className={classnames("mnt_drp_paymentMethod", {
                        invalid__clz: selectInputPM,
                        readOnly__clz: isConsult,
                    })}
                    value={paymentMethod}
                    id="Method_of_payment"
                    name="Method_of_payment"
                    options={list_payment_method}
                    onChange={(e: any) => {
                        if (e !== null) {
                            setPaymentMethod(e);
                        }
                        setIsEdited(true);
                        if (e !== null && Object.keys(e).length > 0) {
                            setSelectInputPM(false);
                        } else {
                            setSelectInputPM(true);
                        }
                    }}
                    placeholder={t("Select")}
                    noOptionsMessage={() => t("No options")}
                    maxMenuHeight="80px"
                />
                {selectInputPM && (
                    <FieldError>
                        {t("Please select a method of payment")}
                    </FieldError>
                )}
            </AvGroup>
            <AvGroup>
                <StyledLabel className="mt-3 required__clz" htmlFor="text">
                    {t("Timeout")}
                    {` (s)`}
                </StyledLabel>
                <StyledTextInput
                    data-testid="monetics-valina-timeout-input"
                    className={classnames("mnt_inp_break", {
                        input__clz: inputTimeout,
                        readOnly__clz: isConsult,
                    })}
                    autocomplete="off"
                    id="timeout"
                    name="timeout"
                    placeholder={t("Write")}
                    type="text"
                    min={0}
                    onChange={(e: any) => {
                        let val = e.target.value;
                        setTimeOut(val);
                        if (
                            !numberPositiveMatch.test(val) ||
                            val.trim() === ""
                        ) {
                            setInputTimeout(true);
                        } else {
                            setInputTimeout(false);
                        }
                        setIsEdited(true);
                    }}
                    value={timeout}
                    validate={{
                        pattern: {
                            value: /.*$/,
                            errorMessage: t("Time out is invalid"),
                        },
                    }}
                />
                {inputTimeout && timeout.trim() === "" ? (
                    <FieldError>
                        {t("Please enter the timeout")!}
                    </FieldError>
                ) : inputTimeout ? (
                    <FieldError>
                        {t("Timeout is invalid")!}
                    </FieldError>
                ) : null}
            </AvGroup>
            <AvGroup>
                <StyledLabel className="mt-3" htmlFor="text">
                    {t("Activate the protocol KISS")}
                </StyledLabel>
                <div className="float-right">
                    <Switch
                        offColor="#faafb4"
                        offHandleColor="#E30613"
                        onColor="#c2eec4"
                        data-testid="monetics-valina-kiss-switch"
                        className={classnames("mnt_icn_kiss mt-3  pl-2", {
                            readOnly__clz: isConsult,
                        })}
                        onHandleColor="#34C38F"
                        uncheckedIcon={false}
                        checkedIcon={false}
                        checked={kiss}
                        height={20}
                        handleDiameter={24}
                        name="activate-protocol-kiss"
                        onChange={(e: any) => {
                            setKiss(!kiss);
                            setIsEdited(true);
                        }}
                    />
                </div>
            </AvGroup>
        </React.Fragment>
    );
}
